// Fields (the Mac's FieldsSheet; design-docs/Plot grid — design.md): the project's fields in order, each opening to its
// name and, for a list, its options — colour from the writer's inks, name, order, remove (asking where documents with
// it should go) — with New Field, Add Fields For (the plot grid's starter sets) and the fields the writer removed, to
// restore. Removing a field keeps its values. Reordering is by Move Up/Down buttons (the Mac drags). Colours are classes,
// one per ink: the page's CSP allows no style attributes.
import { useState } from 'react';
import { INK_COLORS, type Field, type InkColor, type Project } from '@writing-suite/core';
import {
  canEditOptions, canRemoveField, canRenameField, FIELD_NAME_LIMIT, inkOfOptionColor, isStandardField, STARTER_FIELD_SETS,
} from '@writing-suite/core/plan';
import { announce } from '../shell/context.ts';
import {
  addField, addOption, addStarterFields, documentsUsing, documentsWithValue, moveField, moveOption, recolorOption, removeOption, renameField,
  renameOption, setFieldHidden,
} from '../store/fields.ts';
import type { LiveProject } from '../store/project.ts';
import { StorySheet } from './story/common.tsx';

const TYPES: { id: Field['type']; name: string }[] = [
  { id: 'text', name: 'Text' }, { id: 'enum', name: 'List' }, { id: 'boolean', name: 'Checkbox' }, { id: 'date', name: 'Date' }, { id: 'number', name: 'Number' },
];

const typeName = (type: Field['type']) =>
  TYPES.find((t) => t.id === type)?.name ?? { 'story-time': 'Story time', 'entity-ref': 'Cast', tags: 'Tags' }[type as string] ?? type;

const inkName = (ink: string) => ink.charAt(0).toUpperCase() + ink.slice(1);

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** A name field that saves on Return or on leaving it, and goes back to the stored name when that's refused. */
function NameInput({ value, label, disabled, onSave }: { value: string; label: string; disabled?: boolean; onSave: (name: string) => string | null }) {
  const [draft, setDraft] = useState(value);
  const [shown, setShown] = useState(value);
  if (shown !== value) {
    setShown(value);
    setDraft(value);
  }
  const save = () => {
    const next = draft.trim();
    if (next === '' || next === value) return setDraft(value);
    if (onSave(next)) setDraft(value);
  };
  return (
    <label className="field-name-input">
      <span className="visually-hidden">{label}</span>
      <input
        type="text"
        value={draft}
        disabled={disabled}
        maxLength={FIELD_NAME_LIMIT}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            save();
          }
        }}
      />
    </label>
  );
}

function OptionRow({ field, index, project, live, write, onError }: {
  field: Field; index: number; project: Project; live: LiveProject; write: boolean; onError: (e: string | null) => void;
}) {
  const option = field.options[index];
  const using = documentsUsing(project, field.id, option.id);
  const ink = inkOfOptionColor(option.color);
  const colour = option.color === undefined ? 'none' : ink ?? 'own';
  const [removing, setRemoving] = useState(false);
  const [moveTo, setMoveTo] = useState('');
  const others = field.options.filter((o) => o.id !== option.id);
  return (
    <li className="option-row" data-option-id={option.id}>
      <span className={`category-dot ${ink ? `ink-${ink}` : option.color ? 'ink-own' : 'ink-none'}`} aria-hidden="true" />
      <label>
        <span className="visually-hidden">Colour of {option.name}</span>
        <select
          value={colour}
          disabled={!write}
          onChange={(e) => {
            const v = e.target.value;
            if (v === 'own') return;
            onError(recolorOption(live, field.id, option.id, v === 'none' ? null : (v as InkColor)));
          }}
        >
          <option value="none">No colour</option>
          {INK_COLORS.map((i) => <option key={i} value={i}>{inkName(i)}</option>)}
          {colour === 'own' ? <option value="own">Its own</option> : null}
        </select>
      </label>
      <NameInput value={option.name} label={`Name of ${option.name}`} disabled={!write} onSave={(name) => {
        const refused = renameOption(live, field.id, option.id, name);
        onError(refused);
        return refused;
      }} />
      <span className="option-count">{plural(using.length, 'document')}</span>
      {write ? (
        <span className="row-buttons">
          <button type="button" className="link-button" disabled={index === 0} aria-label={`Move ${option.name} up`}
            onClick={() => onError(moveOption(live, field.id, option.id, index - 1))}>↑</button>
          <button type="button" className="link-button" disabled={index === field.options.length - 1} aria-label={`Move ${option.name} down`}
            onClick={() => onError(moveOption(live, field.id, option.id, index + 1))}>↓</button>
          {removing ? null : (
            <button type="button" className="link-button" aria-label={`Remove ${option.name}`} onClick={() => setRemoving(true)}>Remove</button>
          )}
        </span>
      ) : null}
      {removing ? (
        <div className="option-remove" role="group" aria-label={`Remove ${option.name}`}>
          {using.length ? (
            <label>
              Move {plural(using.length, 'document')} with “{option.name}” to:{' '}
              <select value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
                <option value="">No value</option>
                {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
          ) : null}
          <button type="button" className="link-button danger" onClick={() => {
            const refused = removeOption(live, project, field.id, option.id, moveTo || null);
            onError(refused);
            if (!refused) announce(`Removed ${option.name}`);
            setRemoving(false);
          }}>
            Remove it
          </button>
          <button type="button" className="link-button" onClick={() => setRemoving(false)}>Keep</button>
        </div>
      ) : null}
    </li>
  );
}

function FieldDetail({ field, project, live, write, onBack, onError }: {
  field: Field; project: Project; live: LiveProject; write: boolean; onBack: () => void; onError: (e: string | null) => void;
}) {
  const [newOption, setNewOption] = useState('');
  const [confirming, setConfirming] = useState(false);
  const add = () => {
    const name = newOption.trim();
    if (!name) return;
    const refused = addOption(live, field.id, name);
    onError(refused);
    if (!refused) setNewOption('');
  };
  return (
    <section className="field-detail" aria-label={field.name} data-field-id={field.id}>
      <button type="button" className="link-button" onClick={onBack}>‹ Fields</button>
      <div className="field-row">
        <span className="field-label">Name</span>
        <NameInput value={field.name} label="Name" disabled={!write || !canRenameField(field)} onSave={(name) => {
          const refused = renameField(live, field.id, name);
          onError(refused);
          return refused;
        }} />
      </div>
      <p className="sheet-note">
        {typeName(field.type)} · {plural(documentsWithValue(project, field.id), 'document')} with a value
        {isStandardField(field) ? ' · one of the app’s own fields' : ''}
      </p>
      {field.id === 'beat' ? (
        <p className="sheet-note">Beats come from the beat sheet. Edit them on the beat board (Plan ▸ Beats).</p>
      ) : canEditOptions(field) ? (
        <>
          <h3 className="eyebrow">Options</h3>
          <ol className="option-list" aria-label={`Options of ${field.name}`}>
            {field.options.map((o, i) => <OptionRow key={o.id} field={field} index={i} project={project} live={live} write={write} onError={onError} />)}
          </ol>
          {field.id === 'status' ? <p className="sheet-note">The order is the workflow: Next Status and Previous Status follow it.</p> : null}
          {write ? (
            <form className="category-add-row" onSubmit={(e) => { e.preventDefault(); add(); }}>
              <label className="visually-hidden" htmlFor="field-new-option">New option</label>
              <input id="field-new-option" type="text" value={newOption} placeholder="New option" maxLength={FIELD_NAME_LIMIT} onChange={(e) => setNewOption(e.target.value)} />
              <button type="submit" className="button secondary small" disabled={!newOption.trim()}>Add</button>
            </form>
          ) : null}
        </>
      ) : null}
      {write && canRemoveField(field) && !field.hidden ? (
        confirming ? (
          <p className="field-remove">
            Remove “{field.name}”? Its values stay on the documents, and Restore brings it back.{' '}
            <button type="button" className="link-button danger" onClick={() => {
              const refused = setFieldHidden(live, field.id, true);
              onError(refused);
              if (!refused) {
                announce(`Removed ${field.name}`);
                onBack();
              }
            }}>Remove Field</button>{' '}
            <button type="button" className="link-button" onClick={() => setConfirming(false)}>Keep</button>
          </p>
        ) : (
          <button type="button" className="link-button danger" onClick={() => setConfirming(true)}>Remove Field…</button>
        )
      ) : null}
    </section>
  );
}

/** The project's fields: make, rename, reorder, remove and restore them, and edit a list's options. */
export function FieldsDialog({ project, live, write, onClose }: { project: Project; live: LiveProject; write: boolean; onClose: () => void }) {
  const all = project.allFields();
  const shown = all.filter((f) => !f.hidden);
  const removed = all.filter((f) => f.hidden);
  const [openId, setOpenId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [type, setType] = useState<Field['type']>('text');
  const [error, setError] = useState<string | null>(null);
  const open = openId ? all.find((f) => f.id === openId) : undefined;
  const add = () => {
    const next = name.trim();
    if (!next) return;
    const refused = addField(live, next, type);
    setError(refused);
    if (!refused) {
      setName('');
      announce(`Added ${next}`);
    }
  };
  return (
    <StorySheet title="Fields" onClose={onClose} className="fields-sheet">
      {open ? (
        <FieldDetail field={open} project={project} live={live} write={write} onBack={() => setOpenId(null)} onError={setError} />
      ) : (
        <>
          <ol className="field-list" aria-label="Fields">
            {shown.map((f, i) => (
              <li key={f.id} className="field-list-row" data-field-id={f.id}>
                <button type="button" className="field-open" onClick={() => setOpenId(f.id)}>
                  <span className="field-list-name">{f.name}</span>
                  <span className="field-list-meta">
                    {typeName(f.type)}
                    {f.type === 'enum' ? ` · ${plural(f.options.length, 'option')}` : ''} · {plural(documentsWithValue(project, f.id), 'document')}
                  </span>
                </button>
                {write ? (
                  <span className="row-buttons">
                    <button type="button" className="link-button" disabled={i === 0} aria-label={`Move ${f.name} up`}
                      onClick={() => setError(moveField(live, f.id, i > 1 ? shown[i - 2].id : null))}>↑</button>
                    <button type="button" className="link-button" disabled={i === shown.length - 1} aria-label={`Move ${f.name} down`}
                      onClick={() => setError(moveField(live, f.id, shown[i + 1].id))}>↓</button>
                  </span>
                ) : null}
              </li>
            ))}
          </ol>
          <p className="sheet-note">The outliner and inspector show fields in this order.</p>
          {write ? (
            <>
              <form className="category-add" onSubmit={(e) => { e.preventDefault(); add(); }}>
                <label className="field-label stacked-label" htmlFor="field-new">New field</label>
                <div className="category-add-row">
                  <input id="field-new" type="text" value={name} placeholder="Polarity" maxLength={FIELD_NAME_LIMIT} onChange={(e) => setName(e.target.value)} />
                  <label className="visually-hidden" htmlFor="field-new-type">Type</label>
                  <select id="field-new-type" value={type} onChange={(e) => setType(e.target.value as Field['type'])}>
                    {TYPES.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                  <button type="submit" className="button secondary small" disabled={!name.trim()}>Add Field</button>
                </div>
              </form>
              <h3 className="eyebrow">Add fields for</h3>
              <ul className="starter-sets">
                {STARTER_FIELD_SETS.map((set) => (
                  <li key={set.id}>
                    <button type="button" className="link-button" data-starter={set.id} onClick={() => {
                      const refused = addStarterFields(live, set);
                      setError(refused);
                      if (!refused) announce(`Added the ${set.name.toLowerCase()} fields`);
                    }}>{set.name}</button>
                    <span className="field-list-meta"> {set.summary}</span>
                  </li>
                ))}
              </ul>
              <p className="sheet-note">Questions a plot grid asks of every scene. Only the fields the project doesn’t have are added.</p>
            </>
          ) : null}
          {removed.length ? (
            <>
              <h3 className="eyebrow">Removed</h3>
              <ul className="field-list removed" aria-label="Removed fields">
                {removed.map((f) => (
                  <li key={f.id} className="field-list-row" data-field-id={f.id}>
                    <span className="field-list-name">{f.name}</span>
                    {write ? (
                      <button type="button" className="link-button" onClick={() => {
                        setError(setFieldHidden(live, f.id, false));
                        announce(`Restored ${f.name}`);
                      }}>Restore</button>
                    ) : null}
                  </li>
                ))}
              </ul>
              <p className="sheet-note">A removed field’s values stay on the documents.</p>
            </>
          ) : null}
        </>
      )}
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      <div className="actions">
        <button type="button" className="button primary" onClick={onClose}>Done</button>
      </div>
    </StorySheet>
  );
}
